import { useRef, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type ReactEChartsCoreType from 'echarts-for-react/lib/core';
import { TreeChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { breakpointsMediaQuery, getColor, useMediaQuery } from '@/helpers/utils';
import { Button, Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import { useAppContext } from '@/Main';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import type { HierarchicalNode } from '../types';
import { decodeName } from '../utils/decodeName';
import { slugifyFilename } from '../utils/download';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TreeChart,
  CanvasRenderer
]);

type TreeLayout = 'orthogonal' | 'radial' | string;

export type TreeProps = {
  data?: HierarchicalNode | null;
  layout?: TreeLayout;
  title?: string;
};

// نمودار درخت پایه
const Tree = ({ data, layout = 'orthogonal', title = '' }: TreeProps) => {
  const chartRef = useRef<ReactEChartsCoreType | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const [fontSize, setFontSize] = useState(8);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [maxZoomLevel] = useState(20);
  const [minZoomLevel] = useState(0.001);

  const getChart = () => chartRef.current?.getEchartsInstance();

  // کنترل ریست نمودار
  const handleZoomReset = () => {
    setZoomLevel(1);
    getChart()?.dispatchAction({
      type: 'restore'
    });
  };
  // افزودن زوم نمودار
  const handleZoomIn = () => {
    if (zoomLevel < maxZoomLevel) {
      setZoomLevel(zoomLevel + 0.3);
    }
    getChart()?.setOption({
      series: {
        zoom: zoomLevel + 0.3
      }
    });
  };
  //کم کردن زوم نمودار
  const handleZoomOut = () => {
    if (zoomLevel > minZoomLevel) {
      setZoomLevel(zoomLevel - 0.3);
    }
    getChart()?.setOption({
      series: {
        zoom: zoomLevel - 0.3
      }
    });
  };
  // تنظیمات نمودار
  const getOption = (treeData: HierarchicalNode | null | undefined, size: number) => ({
    textStyle: {
      fontFamily: 'dana'
    },
    formatter: (params: unknown) => console.log('FORMATTER', params),
    // نحوه نمایش تولتیپ نمودار
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100'), fontSize: size },
      borderWidth: 1,
      show: false,
      formatter: (params: { name?: string }) => {
        return `<div>
        <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
        <span class="fw-bolder">
        کلمه: 
        </span>
        <span style="direction:ltr;"> ${params.name} </span>
        </div>
        <span class="fw-bolder">
 
        </div>`;
      },
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      }
    },
    series: [
      {
        // تنظیمات نمایشی نمودار
        roam: true,
        layout,
        scaleLimit: {
          min: minZoomLevel,
          max: maxZoomLevel
        },
        type: 'tree',
        data: treeData ? [treeData] : [],
        top: '1%',
        left: '7%',
        bottom: '1%',
        itemStyle: {
          color: getColor('primary'),
          backgroundColor: 'red',
          borderWidth: '30px'
        },
        right: '20%',
        lineStyle: { color: getColor('primary') },
        label: {
          position: 'left',
          verticalAlign: 'middle',
          align: 'right',
          color: getColor('gray-1100'),
          border: 0,
          fontSize: size,
          formatter: (params: { name: string }) => decodeURIComponent(params.name)
        },
        showSymbol: true,
        symbol: 'emptyCircle',

        symbolSize: 10,
        leaves: {
          label: {
            position: 'right',
            verticalAlign: 'middle',
            align: 'left'
          },
          itemStyle: {
            color: getColor('primary'),
            borderRadius: [0, 3, 3, 0]
          }
        },
        expandAndCollapse: true,
        animationDuration: 550,
        animationDurationUpdate: 750
      }
    ]
  });
  const {
    config: { isDark }
  } = useAppContext();
  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <Card className="my-2" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            {title}
            <HelpTooltip html={crawlTooltip.crawlerLinkGraph.tree} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="py-2 my-2 position-relative">
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getOption(data, fontSize)}
          style={{ height: '800px' }}
        />
        {/* دکمه های نمودار */}

        <div className="position-absolute d-flex flex-column top-0 mt-3">
          <div className="rounded-3 bg-body-tertiary border-400 w-100 border">
            <Button
              variant="link"
              size="sm"
              className="bg-100 d-flex align-items-center justify-content-center rounded-bottom-0 p-2 text-700"
              onClick={handleZoomIn}
            >
              <FontAwesomeIcon icon="plus" className="fs--1" />
            </Button>
            <hr className="m-0 text-300" />
            <Button
              variant="link"
              size="sm"
              className="bg-100 d-flex align-items-center justify-content-center rounded-top-0  p-2 text-700"
              onClick={handleZoomReset}
            >
              <FontAwesomeIcon icon="undo-alt" className="fs--1" />
            </Button>
            <hr className="m-0 text-300" />
            <Button
              variant="link"
              size="sm"
              className="bg-100 d-flex align-items-center justify-content-center rounded-top-0  p-2 text-700"
              onClick={handleZoomOut}
            >
              <FontAwesomeIcon icon="minus" className="fs--1" />
            </Button>
          </div>
          <div className="bg-body-tertiary mt-3">
            <div className="bg-body-tertiary mt-1 ">
              <Button
                variant="link"
                size="sm"
                // تمام صفحه کردن نمودار
                onClick={() => {
                  if (!document.fullscreenElement) {
                    void cardRef.current?.requestFullscreen();
                  } else {
                    void document.exitFullscreen();
                  }
                }}
                className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100 p-2 text-700"
              >
                <FontAwesomeIcon
                  icon={
                    document.fullscreenElement
                      ? 'compress-arrows-alt'
                      : 'expand-arrows-alt'
                  }
                  className="fs--1"
                />
              </Button>
            </div>
          </div>
          {/* تغییر دهنده اندازه نمودار */}
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="bottom"
            overlay={
              <Popover id="popover-basic" style={{ width: '350px' }}>
                <Popover.Header
                  as="h3"
                  className="d-flex justify-content-between align-items-center"
                >
                  تغییر اندازه فونت
                  <span className="fs--1" style={{ direction: 'ltr' }}>
                    {fontSize} px
                  </span>
                </Popover.Header>
                <Popover.Body className="p-0">
                  <div
                    className="p-2 d-flex align-items-center"
                    style={{ direction: 'ltr' }}
                  >
                    <Form.Range
                      max={50}
                      min={0}
                      value={fontSize}
                      onChange={e => setFontSize(Number(e.target.value))}
                    />
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <div className="bg-body-tertiary mt-1 ">
              <Button
                variant="link"
                size="sm"
                className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100  p-2 text-700"
              >
                <FontAwesomeIcon icon="text-height" className="fs--1" />
              </Button>
            </div>
          </OverlayTrigger>
          <div className="bg-body-tertiary mt-1 ">
            <Button
              onClick={() => {
                // ذخیره کننده عکس نمودار
                const dataURL = getChart()?.getDataURL({
                  type: 'png',
                  pixelRatio: 1,
                  backgroundColor: isDark ? '#121e2d' : '#fff'
                });
                if (!dataURL) return;
                const stem = slugifyFilename(
                  decodeName(data?.name),
                  layout === 'radial' ? 'radial' : 'basic'
                );
                const link = document.createElement('a');
                link.href = dataURL;
                link.setAttribute(
                  'download',
                  `mizfa-tools-cluster-${stem}-tree.png`
                );
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              variant="link"
              size="sm"
              className="bg-100 d-flex align-items-center justify-content-center rounded-3 border border-400 w-100  p-2 text-700"
            >
              <FontAwesomeIcon icon="save" className="fs--1" />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default Tree;
